import { useState, useRef, useCallback, useEffect } from 'react'
import { useNavigate } from 'react-router-dom'
import { Loader2, AlertCircle, Package, X, Search, User } from 'lucide-react'
import { Input } from '@/components/ui/input'
import { useOrderSearch } from '@/hooks/useOrderSearch'
import { useExcludedPickupPrefixes, shouldAutoSearchPickupCode } from '@/hooks/usePickupCode'
import { supabase } from '@/lib/supabase'
import { cn } from '@/lib/utils'

type SearchMode = 'order' | 'customer' | null

// Debounce hook
function useDebounce<T extends (...args: Parameters<T>) => void>(
  callback: T,
  delay: number
): T {
  const timeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null)

  const debouncedCallback = useCallback((...args: Parameters<T>) => {
    if (timeoutRef.current) {
      clearTimeout(timeoutRef.current)
    }
    timeoutRef.current = setTimeout(() => {
      callback(...args)
    }, delay)
  }, [callback, delay]) as T

  return debouncedCallback
}

export function SearchBar() {
  const [query, setQuery] = useState('')
  const [searchMode, setSearchMode] = useState<SearchMode>(null)
  const orderInputRef = useRef<HTMLInputElement>(null)
  const customerInputRef = useRef<HTMLInputElement>(null)
  const navigate = useNavigate()
  const {
    isSearching,
    error,
    customerMatches,
    searchAndNavigate,
    searchByPickupCode,
    searchCustomers,
    clearError,
    clearResults
  } = useOrderSearch()

  // Get excluded pickup code prefixes for auto-search detection
  // Default to ['6', '7'] which covers current order number range (6xxxxx-7xxxxx)
  const { data: excludedPrefixes } = useExcludedPickupPrefixes()
  const prefixes = excludedPrefixes ?? ['6', '7']

  // Debounced customer search (300ms delay)
  const debouncedSearch = useDebounce(searchCustomers, 300)

  // Focus input when mode changes
  useEffect(() => {
    if (searchMode === 'order') {
      // Small delay to allow transition to start
      setTimeout(() => orderInputRef.current?.focus(), 50)
    } else if (searchMode === 'customer') {
      setTimeout(() => customerInputRef.current?.focus(), 50)
    }
  }, [searchMode])

  // Handle button click - focus input synchronously
  const handleOrderClick = () => {
    setSearchMode('order')
    setQuery('')
    clearError()
    clearResults()
    // Focus synchronously in click handler
    orderInputRef.current?.focus()
  }

  const handleCustomerClick = () => {
    setSearchMode('customer')
    setQuery('')
    clearError()
    clearResults()
    // Focus synchronously in click handler
    customerInputRef.current?.focus()
  }

  const handleClose = () => {
    setSearchMode(null)
    setQuery('')
    clearError()
    clearResults()
  }

  const handleOrderChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const value = e.target.value
    setQuery(value)
    clearError()
    clearResults()

    const digitsOnly = value.replace(/\D/g, '')

    // Check for pickup code (4 digits, not starting with excluded prefix)
    if (shouldAutoSearchPickupCode(digitsOnly, prefixes)) {
      orderInputRef.current?.blur()
      searchByPickupCode(digitsOnly)
      return
    }

    // Auto-search when 6 digits are entered (order number)
    if (digitsOnly.length === 6) {
      orderInputRef.current?.blur()
      searchAndNavigate(digitsOnly, 'manual')
    }
  }

  const handleCustomerChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const value = e.target.value
    setQuery(value)
    clearError()

    // Debounced autocomplete search
    if (value.length >= 2) {
      debouncedSearch(value)
    } else {
      clearResults()
    }
  }

  const handleKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) => {
    if (e.key === 'Escape') {
      handleClose()
      return
    }
    if (e.key === 'Enter' && query.trim() && searchMode === 'order') {
      const digitsOnly = query.trim().replace(/\D/g, '')

      // Check for pickup code (4 digits, not starting with excluded prefix)
      if (shouldAutoSearchPickupCode(digitsOnly, prefixes)) {
        searchByPickupCode(digitsOnly)
        return
      }

      // Order number search (5+ digits, or 4 digits starting with excluded prefix)
      if (digitsOnly.length >= 4) {
        searchAndNavigate(digitsOnly, 'manual')
      }
    }
  }

  const handleCustomerSelect = async (customerId: number) => {
    // Check pending orders for this customer
    const { data: pendingOrders } = await supabase
      .schema('delivery').from('app_orders')
      .select('order_id')
      .eq('customer_id', customerId)
      .eq('status', 'pending')

    // If exactly 1 pending order, go directly to PendingView
    if (pendingOrders && pendingOrders.length === 1) {
      navigate(`/delivery/${pendingOrders[0].order_id}/pending`)
      return
    }

    // Otherwise, show all customer orders
    navigate(`/customer/${customerId}`)
  }

  return (
    <div className="space-y-3">
      {/* Fixed height container to prevent layout shifts */}
      <div className="relative h-24">
        {/* Two buttons - shown when no mode selected */}
        <div className={cn(
          'absolute inset-0 grid grid-cols-2 gap-4 transition-all duration-200',
          searchMode === null
            ? 'opacity-100 pointer-events-auto'
            : 'opacity-0 pointer-events-none scale-95'
        )}>
          <button
            type="button"
            onClick={handleOrderClick}
            className="h-full flex flex-col items-center justify-center gap-2 rounded-2xl bg-emerald-500 hover:bg-emerald-600 transition-colors shadow-sm"
          >
            <Search className="h-8 w-8 text-white" strokeWidth={2.5} />
            <span className="text-sm font-medium text-white">Ordre</span>
          </button>
          <button
            type="button"
            onClick={handleCustomerClick}
            className="h-full flex flex-col items-center justify-center gap-2 rounded-2xl bg-blue-500 hover:bg-blue-600 transition-colors shadow-sm"
          >
            <User className="h-8 w-8 text-white" strokeWidth={2.5} />
            <span className="text-sm font-medium text-white">Kunde</span>
          </button>
        </div>

        {/* Order input - absolutely positioned */}
        <div className={cn(
          'absolute inset-0 flex items-center transition-all duration-200',
          searchMode === 'order'
            ? 'opacity-100 pointer-events-auto'
            : 'opacity-0 pointer-events-none scale-95'
        )}>
          <div className="relative w-full">
            {isSearching && (
              <Loader2 className="absolute left-4 top-1/2 -translate-y-1/2 h-5 w-5 text-primary animate-spin z-10" />
            )}
            <Input
              ref={orderInputRef}
              type="text"
              inputMode="numeric"
              value={searchMode === 'order' ? query : ''}
              onChange={handleOrderChange}
              onKeyDown={handleKeyDown}
              placeholder="Skriv ordrenummer..."
              className={cn(
                "h-14 pr-12 text-lg bg-secondary border-border placeholder:text-muted-foreground",
                isSearching ? "pl-12" : "pl-4"
              )}
              disabled={isSearching}
            />
            <button
              type="button"
              onClick={handleClose}
              className="absolute right-3 top-1/2 -translate-y-1/2 p-1.5 rounded-md hover:bg-accent transition-colors z-10"
              aria-label="Lukk sok"
            >
              <X className="h-5 w-5 text-muted-foreground" />
            </button>
          </div>
        </div>

        {/* Customer input - absolutely positioned */}
        <div className={cn(
          'absolute inset-0 flex items-center transition-all duration-200',
          searchMode === 'customer'
            ? 'opacity-100 pointer-events-auto'
            : 'opacity-0 pointer-events-none scale-95'
        )}>
          <div className="relative w-full">
            {isSearching && (
              <Loader2 className="absolute left-4 top-1/2 -translate-y-1/2 h-5 w-5 text-primary animate-spin z-10" />
            )}
            <Input
              ref={customerInputRef}
              type="text"
              inputMode="text"
              value={searchMode === 'customer' ? query : ''}
              onChange={handleCustomerChange}
              onKeyDown={handleKeyDown}
              placeholder="Skriv kundenavn..."
              className={cn(
                'h-14 pr-12 text-lg bg-secondary border-border placeholder:text-muted-foreground',
                isSearching ? 'pl-12' : 'pl-4',
                customerMatches.length > 0 && 'rounded-b-none border-b-0'
              )}
            />
            <button
              type="button"
              onClick={handleClose}
              className="absolute right-3 top-1/2 -translate-y-1/2 p-1.5 rounded-md hover:bg-accent transition-colors z-10"
              aria-label="Lukk sok"
            >
              <X className="h-5 w-5 text-muted-foreground" />
            </button>

            {/* Customer autocomplete dropdown */}
            {customerMatches.length > 0 && (
              <div className="absolute top-full left-0 right-0 bg-secondary border border-border border-t-0 rounded-b-lg overflow-hidden shadow-lg z-20">
                {customerMatches.map((match, index) => (
                  <button
                    key={match.customer_id}
                    type="button"
                    onClick={() => handleCustomerSelect(match.customer_id)}
                    className={cn(
                      'w-full flex items-center justify-between px-4 py-3 hover:bg-accent transition-colors text-left',
                      index < customerMatches.length - 1 && 'border-b border-border'
                    )}
                  >
                    <span className="font-medium text-foreground truncate flex-1">
                      {match.customer_name}
                    </span>
                    <span className="flex items-center gap-1 text-sm text-muted-foreground ml-2 flex-shrink-0">
                      <Package className="h-4 w-4" />
                      {match.order_count}
                    </span>
                  </button>
                ))}
              </div>
            )}
          </div>
        </div>
      </div>

      {/* Error message */}
      {error && (
        <div className="flex items-center justify-center gap-2 text-destructive text-sm">
          <AlertCircle className="h-4 w-4" />
          <span>{error}</span>
        </div>
      )}
    </div>
  )
}
